import { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';

export default function DebugLocalVideoPlayer() {
  const styles = useStyles();

  const videoSource = useMemo(() => require('@/assets/video/intro.mp4'), []);

  const videoPlayer = useVideoPlayer(videoSource, (player) => {
    player.loop = true;
  });

  const [videoIsReady, setVideoIsReady] = useState(false);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);

  useEffect(() => {
    const statusSub = videoPlayer.addListener('statusChange', (payload) => {
      setVideoIsReady(payload.status === 'readyToPlay');
      if (payload.status === 'error') {
        console.error('[Explore] Video player error:', payload.error);
      }
    });

    const playingSub = videoPlayer.addListener('playingChange', (payload) => {
      setVideoIsPlaying(payload.isPlaying);
    });

    return () => {
      statusSub.remove();
      playingSub.remove();
    };
  }, [videoPlayer]);

  const toggleVideo = () => {
    if (videoPlayer.playing) {
      videoPlayer.pause();
    } else {
      videoPlayer.play();
    }
  };

  const replayVideo = () => {
    videoPlayer.replay();
  };

  return (
    <>
      <ThemedText style={styles.mutedText}>
        {videoIsReady ? 'intro.mp4' : 'Loading…'}
      </ThemedText>

      <View style={styles.videoContainer}>
        <VideoView
          player={videoPlayer}
          style={styles.video}
          nativeControls={true}
          contentFit="contain"
        />
      </View>

      <View style={styles.row}>
        <Pressable
          onPress={toggleVideo}
          disabled={!videoIsReady}
          style={({ focused }) => [
            styles.button,
            focused && styles.buttonFocused,
            !videoIsReady && styles.buttonDisabled,
          ]}
        >
          <ThemedText style={styles.buttonText}>
            {videoIsPlaying ? 'Pause' : 'Play'}
          </ThemedText>
        </Pressable>

        <Pressable
          onPress={replayVideo}
          disabled={!videoIsReady}
          style={({ focused }) => [
            styles.button,
            focused && styles.buttonFocused,
            !videoIsReady && styles.buttonDisabled,
          ]}
        >
          <ThemedText style={styles.buttonText}>Replay</ThemedText>
        </Pressable>
      </View>
    </>
  );
}

function useStyles() {
  const scale = useScale();
  return StyleSheet.create({
    mutedText: {
      color: 'rgba(255,255,255,0.7)',
    },
    videoContainer: {
      width: '100%',
    },
    video: {
      width: '100%',
      height: 220 * scale,
    },
    row: {
      flexDirection: 'row',
      gap: 12 * scale,
      alignItems: 'center',
    },
    button: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 18 * scale,
      borderRadius: 10 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    buttonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    buttonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    buttonDisabled: {
      opacity: 0.4,
    },
  });
}
